import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Card, Empty, Form, Input, message, Modal,
  Popconfirm, Spin, Table, Tooltip,
} from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  DeleteOutlined,
  CalendarOutlined,
  LineChartOutlined,
  WalletOutlined,
} from '@ant-design/icons';

import apiClient from '../../../services/apiClient';
import { isGovtPortfolioHolding } from '../../../services/saveEnginePortfolio';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import { usePortfolioUser } from './usePortfolioUser';
import {
  fmt, currency, CashFlowPanel, CallPutPanel,
} from './portfolioHoldingPanels';
import './PortfolioTables.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

function formatEnumToDisplay(enumValue) {
  if (!enumValue) return '';
  return enumValue
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .replace(/\bAnd\b/g, '&');
}

/** Expanded row: structured position snapshot (Wise-style hierarchy). */
function PositionDetailsSection({ record, includeTypeColumn, formatType }) {
  const couponStr = record.coupon == null ? '—' : `${record.coupon}%`;
  const yieldStr = record.yield == null ? '—' : `${record.yield}%`;

  const dateRows = [
    { k: 'Issue date', v: fmt(record.issueDate) },
    { k: 'Maturity', v: fmt(record.maturityDate) },
    { k: 'Quantity', v: fmt(record.quantity) },
  ];

  const priceRows = [{ k: 'Indicative price', v: currency(record.price) }];

  return (
    <div className="holding-position-card">
      <div className="holding-position-hero">
        <div className="holding-position-hero-icon" aria-hidden>
          <WalletOutlined />
        </div>
        <div className="holding-position-hero-text">
          <span className="holding-position-hero-label">Your position value</span>
          <span className="holding-position-hero-value">{currency(record.totalMarketValue)}</span>
        </div>
        <div className="holding-position-hero-pills">
          <div className="holding-position-pill">
            <span className="holding-position-pill-label">Coupon</span>
            <span className="holding-position-pill-value">{couponStr}</span>
          </div>
          <div className="holding-position-pill">
            <span className="holding-position-pill-label">Yield</span>
            <span className="holding-position-pill-value">{yieldStr}</span>
          </div>
        </div>
      </div>

      <div className="holding-position-sheet">
        <div className="holding-position-group">
          <div className="holding-position-group-title">
            <CalendarOutlined className="holding-position-group-ico" aria-hidden />
            Schedule & size
          </div>
          <ul className="holding-position-kv">
            {dateRows.map((row) => (
              <li key={row.k} className="holding-position-kv-row">
                <span className="holding-position-kv-key">{row.k}</span>
                <span className="holding-position-kv-val">{row.v}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="holding-position-divider" aria-hidden />
        <div className="holding-position-group">
          <div className="holding-position-group-title">
            <LineChartOutlined className="holding-position-group-ico" aria-hidden />
            Market quote
          </div>
          <ul className="holding-position-kv">
            {priceRows.map((row) => (
              <li key={row.k} className="holding-position-kv-row">
                <span className="holding-position-kv-key">{row.k}</span>
                <span className="holding-position-kv-val">{row.v}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="holding-position-footer">
        <div className="holding-position-isin-block">
          <span className="holding-position-isin-label">ISIN</span>
          <code className="holding-position-isin-code">{fmt(record.isin)}</code>
        </div>
        {includeTypeColumn && record.type && (
          <span className="holding-position-strategy-pill">{formatType(record.type)}</span>
        )}
      </div>
    </div>
  );
}

export default function MyPortfolioHoldingsTable({
  type,
  refreshSignal = 0,
  includeTypeColumn = false,
  title,
  emptyText = 'No holdings for this strategy',
  onRowsChanged,
  holdingsData,
  holdingsLoading = false,
  onRequestReload,
}) {
  const navigate = useNavigate();
  const { userId, loading: userLoading, isAuthenticated, requireAuth, authHeaders } = usePortfolioUser();
  const controlled = holdingsData != null;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingIsin, setDeletingIsin] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm] = Form.useForm();
  const [expandedKeys, setExpandedKeys] = useState([]);

  const fetchHoldings = useCallback(async () => {
    if (controlled) {
      if (onRequestReload) onRequestReload();
      return;
    }
    if (!userId) {
      setRows([]);
      if (onRowsChanged) onRowsChanged([]);
      return;
    }
    try {
      setLoading(true);
      const res = await apiClient.get(`${BASE_URL}/user/portfolio/list/${userId}`, {
        ...(type ? { params: { type } } : {}),
      });
      const nextRows = res.data?.data || [];
      setRows(nextRows);
      if (onRowsChanged) onRowsChanged(nextRows);
    } catch (e) {
      console.error('Failed to load portfolio holdings', e);
      message.error(getApiErrorMessage(e, 'Failed to load portfolio holdings'));
      setRows([]);
      if (onRowsChanged) onRowsChanged([]);
    } finally {
      setLoading(false);
    }
  }, [controlled, onRequestReload, type, onRowsChanged, userId]);

  useEffect(() => {
    if (controlled) {
      setRows(holdingsData);
    }
  }, [controlled, holdingsData]);

  useEffect(() => {
    if (!controlled) {
      fetchHoldings();
    }
  }, [controlled, fetchHoldings, refreshSignal]);

  const tableLoading = controlled ? holdingsLoading : (userLoading || loading);

  const columns = useMemo(
    () => [
      {
        title: 'Bond',
        key: 'bond',
        width: 280,
        ellipsis: true,
        align: 'left',
        render: (_, row) => (
          <div className="holding-bond-cell">
            <div className="holding-bond-issuer" title={fmt(row.issuer)}>
              {fmt(row.issuer)}
            </div>
            <div className="holding-bond-isin">{fmt(row.isin)}</div>
          </div>
        ),
      },
      {
        title: 'Maturity',
        dataIndex: 'maturityDate',
        key: 'maturityDate',
        width: 120,
        align: 'left',
        render: fmt,
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 72,
        align: 'right',
        render: fmt,
      },
      {
        title: 'Market value',
        dataIndex: 'totalMarketValue',
        key: 'totalMarketValue',
        width: 140,
        align: 'right',
        render: (v) => <span className="holding-mv-value">{currency(v)}</span>,
      },
      {
        title: 'Coupon / Yield',
        key: 'returns',
        width: 118,
        align: 'right',
        render: (_, row) => (
          <div className="holding-returns-cell">
            <span>{row.coupon == null ? '—' : `${row.coupon}%`}</span>
            <span className="holding-returns-sep">/</span>
            <span>{row.yield == null ? '—' : `${row.yield}%`}</span>
          </div>
        ),
      },
      ...(includeTypeColumn ? [{
        title: 'Strategy',
        dataIndex: 'type',
        key: 'type',
        width: 130,
        align: 'left',
        ellipsis: true,
        render: (v) => (v ? formatEnumToDisplay(v) : '—'),
      }] : []),
      {
        title: 'Actions',
        key: 'action',
        width: 112,
        align: 'center',
        render: (_, row) => (
          <div style={{ display: 'flex', gap: 8 }}>
            {!isGovtPortfolioHolding(row) && (
              <Tooltip title="View ISIN">
                <Button
                  size="small"
                  type="text"
                  icon={<EyeOutlined style={{ color: '#2563eb' }} />}
                  onClick={() => navigate(`/isin-details/${row.isin}`)}
                />
              </Tooltip>
            )}

            <Tooltip title="Edit Quantity">
              <Button
                size="small"
                type="text"
                icon={<EditOutlined style={{ color: '#059669' }} />}
                onClick={() => {
                  setEditingRow(row);
                  editForm.setFieldsValue({ quantity: row.quantity });
                  setEditModalOpen(true);
                }}
              />
            </Tooltip>

            <Popconfirm
              title="Remove holding?"
              description="This will remove the ISIN from your portfolio."
              okText="Remove"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              disabled={deletingIsin === row.isin}
              onConfirm={async () => {
                const auth = requireAuth('Please log in to remove portfolio holdings');
                if (!auth.ok) return;
                try {
                  setDeletingIsin(row.isin);
                  await apiClient.delete(`${BASE_URL}/user/portfolio/${row.isin}`, {
                    headers: authHeaders(),
                  });
                  message.success('Removed from portfolio');
                  setRows((prev) => {
                    const next = prev.filter((r) => r.isin !== row.isin);
                    if (onRowsChanged) onRowsChanged(next);
                    return next;
                  });
                } catch (e) {
                  console.error('Failed to delete holding', e);
                  message.error(getApiErrorMessage(e, 'Failed to remove holding'));
                } finally {
                  setDeletingIsin(null);
                }
              }}
            >
              <Tooltip title="Remove">
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingIsin === row.isin}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        ),
      },
    ],
    [navigate, deletingIsin, editForm, includeTypeColumn, onRowsChanged, requireAuth, authHeaders]
  );

  const typeLabel = useMemo(() => {
    if (!type) return 'Portfolio';
    return String(type)
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, [type]);

  return (
    <>
      <Card
        className="portfolio-card"
        title={title || `My ${typeLabel}`}
        extra={(
          <Button icon={<ReloadOutlined />} onClick={fetchHoldings} loading={loading}>
            Refresh
          </Button>
        )}
      >
        {tableLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
        ) : !isAuthenticated ? (
          <Empty description="Log in to view and manage your portfolio holdings" style={{ padding: 24 }}>
            <Button type="primary" onClick={() => requireAuth()}>
              Log in
            </Button>
          </Empty>
        ) : rows.length === 0 ? (
          <Empty description={emptyText} style={{ padding: 24 }} />
        ) : (
          <>
            <p className="portfolio-table-hint">
              Expand <strong>+</strong> for full position details, cash flows, and call/put data.
            </p>
            <Table
              className="portfolio-table portfolio-table--comfortable"
              rowKey={(r) => r.isin}
              columns={columns}
              dataSource={rows}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              tableLayout="fixed"
              expandable={{
                expandedRowKeys: expandedKeys,
                onExpand: (expanded, record) => {
                  setExpandedKeys(
                    expanded
                      ? [...expandedKeys, record.isin]
                      : expandedKeys.filter((k) => k !== record.isin)
                  );
                },
                expandedRowRender: (record) => (
                  <div className="holding-expand">
                    <PositionDetailsSection
                      record={record}
                      includeTypeColumn={includeTypeColumn}
                      formatType={formatEnumToDisplay}
                    />
                    <CashFlowPanel isin={record.isin} createdAt={record.createdAt} />
                    <CallPutPanel isin={record.isin} />
                  </div>
                ),
                rowExpandable: (record) => !!record.isin,
                expandRowByClick: false,
              }}
            />
          </>
        )}
      </Card>

      <Modal
        title={editingRow?.isin ? `Edit Quantity — ${editingRow.isin}` : 'Edit Quantity'}
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingRow(null);
          editForm.resetFields();
        }}
        okText="Update"
        confirmLoading={editSaving}
        onOk={async () => {
          if (!editingRow?.isin) return;
          const auth = requireAuth('Please log in to update portfolio holdings');
          if (!auth.ok) return;
          try {
            const values = await editForm.validateFields();
            setEditSaving(true);
            await apiClient.patch(
              `${BASE_URL}/user/portfolio/${editingRow.isin}/quantity`,
              values,
              { headers: authHeaders() }
            );
            message.success('Quantity updated');
            setEditModalOpen(false);
            setEditingRow(null);
            editForm.resetFields();
            if (controlled && onRequestReload) {
              onRequestReload();
            } else {
              fetchHoldings();
            }
          } catch (e) {
            if (e?.errorFields) return;
            console.error('Failed to update quantity', e);
            message.error(getApiErrorMessage(e, 'Failed to update quantity'));
          } finally {
            setEditSaving(false);
          }
        }}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Quantity"
            name="quantity"
            rules={[{ required: true, message: 'Please enter quantity' }]}
          >
            <Input placeholder="e.g. 10" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
